// admin-auth rejects a cookie once its Google token is this many seconds past
// `token_validity` (bhanzu_auth.validate_credentials: `time_expired > 43_200`).
// Mirrored so the client gate signs out exactly when the API starts to 401.
export const SESSION_GRACE_SECONDS = 43200

/**
 * Decode the bhanzu-admin-auth cookie into `{ email, permissions }`, or `{}`.
 *
 * The cookie is NOT a JWT. admin-auth's sign_credentials writes
 * base64(JSON.stringify({token, refresh_token, token_validity, user_id,
 * issued_time, email, permissions, hash})): one segment, no dots, no `exp`.
 * jwt-decode reads `token.split('.')[1]`, which is undefined here, so it
 * throws on every real cookie (that is why v1 at /old renders blank for a
 * signed-in user). Decoding it as base64 JSON is the format's actual contract,
 * the same thing applause's parseJwt does.
 *
 * Signed out (returns `{}`) when the cookie is missing, unparseable, has no
 * email, has no numeric token_validity, or is past the server's grace window.
 * Only email and permissions are returned: the encrypted token and refresh
 * token never enter app state.
 *
 * The signature (`hash`) is not checked here and cannot be: the secret is
 * server-side. This is a usability gate; the API authorises every call.
 *
 * @param {string|undefined} raw  cookie value as js-cookie returns it
 * @param {number} [nowSeconds]   current time, injectable for tests
 * @returns {{email?: string, permissions?: string[]}}
 */
export const decodeAuthCookie = (raw, nowSeconds = Date.now() / 1000) => {
    if (!raw) return {}
    try {
        const b64 = raw
            .replace(/^"+|"+$/g, '')
            .replace(/-/g, '+')
            .replace(/_/g, '/')
        // atob yields one char per byte; percent-encode each byte and let
        // decodeURIComponent re-read them as UTF-8.
        const utf8 = decodeURIComponent(
            Array.from(
                atob(b64),
                (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`
            ).join('')
        )
        const creds = JSON.parse(utf8)
        if (!creds || typeof creds.email !== 'string' || !creds.email) return {}
        if (
            typeof creds.token_validity !== 'number' ||
            nowSeconds - creds.token_validity > SESSION_GRACE_SECONDS
        )
            return {}
        return {
            email: creds.email,
            permissions: Array.isArray(creds.permissions)
                ? creds.permissions
                : [],
        }
    } catch {
        return {}
    }
}
